import { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { Check, Star, Zap, Crown } from 'lucide-react';

const ServicesSection = () => {
  const { t, language } = useLanguage();
  const { addToCart } = useCart();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [activeTab, setActiveTab] = useState(0);

  const services = [
    {
      id: 'pack-essentiel',
      image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=800&q=80',
      nameKey: 'services.pack1.name',
      priceKey: 'services.pack1.price',
      price: 1200,
      icon: Star,
      color: 'from-blue-500 to-cyan-500',
      features: language === 'fr' 
        ? ['Stratégie marketing', 'Gestion réseaux sociaux', 'Rapport mensuel', 'Support email']
        : ['Marketing strategy', 'Social media management', 'Monthly report', 'Email support'],
    },
    {
      id: 'pack-developpement',
      image: 'https://images.unsplash.com/photo-1551024709-8f23befc6f87?auto=format&fit=crop&w=800&q=80',
      nameKey: 'services.pack2.name',
      priceKey: 'services.pack2.price',
      price: 1800,
      icon: Zap,
      color: 'from-violet-500 to-purple-500',
      popular: true,
      features: language === 'fr' 
        ? ['Tout du Pack Essentiel', 'Campagnes publicitaires', 'SEO avancé', 'Support prioritaire', 'Analytics détaillés']
        : ['Everything in Essential', 'Ad campaigns', 'Advanced SEO', 'Priority support', 'Detailed analytics'],
    },
    {
      id: 'pack-experience',
      image: 'https://images.unsplash.com/photo-1559339352-11d035aa65de?auto=format&fit=crop&w=800&q=80',
      nameKey: 'services.pack3.name',
      priceKey: 'services.pack3.price',
      price: 2500,
      icon: Crown,
      color: 'from-amber-500 to-orange-500',
      features: language === 'fr' 
        ? ['Tout du Pack Développement', 'Stratégie sur mesure', 'Account manager dédié', 'Support 24/7', 'Formation équipe', 'Événements exclusifs']
        : ['Everything in Development', 'Custom strategy', 'Dedicated account manager', '24/7 support', 'Team training', 'Exclusive events'],
    },
  ];

  const handleAddToCart = (service) => {
    addToCart({
      id: service.id,
      name: t(service.nameKey),
      price: service.price,
      image: service.image,
    });
    toast.success(language === 'fr' ? 'Ajouté au panier' : 'Added to cart');
  };

  const tabs = [
    { id: 0, label: language === 'fr' ? 'Tous les packs' : 'All packs' },
    { id: 1, label: language === 'fr' ? 'Populaire' : 'Popular' },
    { id: 2, label: language === 'fr' ? 'Premium' : 'Premium' },
  ];

  const filteredServices = activeTab === 0 
    ? services 
    : activeTab === 1 
      ? services.filter(s => s.popular) 
      : services.filter(s => s.price >= 2500);

  return (
    <section 
      id="services"
      ref={ref}
      className="py-24 md:py-32 bg-white"
      data-testid="services-section"
    >
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            className="inline-block text-xs font-semibold tracking-[0.3em] uppercase text-neutral-500 mb-4"
          >
            {language === 'fr' ? 'Nos Offres' : 'Our Offers'}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
            className="heading-xl text-4xl md:text-5xl lg:text-6xl text-black"
            data-testid="services-title"
          >
            {t('services.title')}
          </motion.h2>
        </div>

        {/* Interactive Tabs */}
        <motion.div 
          className="flex justify-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
        >
          <div className="inline-flex bg-neutral-100 rounded-full p-1.5">
            {tabs.map((tab) => (
              <motion.button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative px-6 py-3 text-sm font-medium rounded-full transition-all duration-300 ${
                  activeTab === tab.id 
                    ? 'text-white' 
                    : 'text-neutral-600 hover:text-black'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid={`tab-${tab.id}`}
              >
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 bg-black rounded-full"
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
                <span className="relative z-10">{tab.label}</span>
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Services Grid */}
        <AnimatePresence mode="wait">
          <motion.div 
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredServices.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 40 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  whileHover={{ y: -8, transition: { duration: 0.3 } }}
                  transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                  className={`relative bg-white rounded-2xl overflow-hidden shadow-lg border border-neutral-100 hover:shadow-2xl transition-shadow ${
                    service.popular ? 'ring-2 ring-black' : ''
                  }`}
                  data-testid={`service-card-${index}`}
                >
                  {/* Popular Badge */}
                  {service.popular && (
                    <div className="absolute top-4 right-4 z-20 bg-black text-white text-xs font-bold px-3 py-1 rounded-full">
                      {language === 'fr' ? 'POPULAIRE' : 'POPULAR'}
                    </div>
                  )}

                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={t(service.nameKey)}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                      data-testid={`service-image-${index}`}
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${service.color} opacity-50`} />
                    <div className="absolute bottom-4 left-4 w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
                      <IconComponent className="w-6 h-6 text-black" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 md:p-8">
                    <h3 
                      className="font-serif text-xl md:text-2xl font-semibold mb-2 text-black"
                      data-testid={`service-name-${index}`}
                    >
                      {t(service.nameKey)}
                    </h3>
                    <p 
                      className="font-sans text-3xl font-bold text-black mb-6"
                      data-testid={`service-price-${index}`}
                    >
                      {t(service.priceKey)}
                      <span className="text-sm font-normal text-neutral-500">
                        {language === 'fr' ? ' /mois' : ' /month'}
                      </span>
                    </p>

                    {/* Features List */}
                    <ul className="space-y-3 mb-6">
                      {service.features.map((feature, i) => (
                        <motion.li 
                          key={i}
                          initial={{ opacity: 0, x: -20 }}
                          animate={isInView ? { opacity: 1, x: 0 } : {}}
                          transition={{ delay: 0.4 + i * 0.05 }}
                          className="flex items-center gap-3 text-sm text-neutral-600"
                        >
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {feature}
                        </motion.li>
                      ))}
                    </ul>

                    <motion.button
                      onClick={() => handleAddToCart(service)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`w-full py-4 rounded-xl font-semibold transition-all duration-300 ${
                        service.popular 
                          ? 'bg-black text-white hover:bg-neutral-800' 
                          : 'bg-neutral-100 text-black hover:bg-neutral-200'
                      }`}
                      data-testid={`service-cta-${index}`}
                    >
                      {t('services.cta')}
                    </motion.button>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </AnimatePresence>

        {/* Stats Section */}
        <motion.div 
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8 }}
        >
          {[
            { value: '150+', label: language === 'fr' ? 'Clients satisfaits' : 'Happy clients' },
            { value: '98%', label: language === 'fr' ? 'Taux de rétention' : 'Retention rate' },
            { value: '5M+', label: language === 'fr' ? 'Impressions/mois' : 'Impressions/month' },
            { value: '24/7', label: language === 'fr' ? 'Support disponible' : 'Support available' },
          ].map((stat, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.03, y: -3 }}
              className="text-center p-6 bg-neutral-50 rounded-2xl border border-neutral-100"
            >
              <div className="font-serif text-3xl md:text-4xl font-bold text-black mb-2">
                {stat.value}
              </div>
              <div className="text-sm text-neutral-500">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
